import {
    CloudDownload,
    WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import ModalityChip from '~/components/Chip/ModalityChip';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import SelectableListItem from '~/components/SelectableListItem/SelectableListItem';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { formatSeriesDate } from '~/utils/date';
import { describeUnloadedImage } from './describeUnloadedImage';
import ImageItem from './ImageItem';
import UnloadedImageTooltip from './UnloadedImageTooltip';

interface AvailableImageItemProps {
    image: AvailableImage;
    isSecondary?: boolean;
    hasSharedFrameOfReference?: boolean;
    isSelected?: boolean;
    onSelect?: () => void;
    onDownload?: () => void;
    onReformat?: () => void;
    onRename?: () => void;
    onDelete?: () => void;
}

/**
 * Unified image list item that handles all AvailableImage states:
 * - Loaded Image3D → delegates to ImageItem
 * - Raw series (needs reformat) → simple row with warning indicator
 * - Session/remote (not loaded) → simple row with download action
 */
export default function AvailableImageItem({
    image,
    isSecondary,
    hasSharedFrameOfReference,
    isSelected,
    onSelect,
    onDownload,
    onReformat,
    onRename,
    onDelete,
}: AvailableImageItemProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);

    if (image.imageObject) {
        return (
            <ImageItem
                image={image.imageObject}
                isSecondary={isSecondary}
                hasSharedFrameOfReference={hasSharedFrameOfReference}
                isSelected={isSelected}
                onSelect={onSelect}
                onRename={onRename}
                onDelete={onDelete}
            />
        );
    }

    const { needsReformat, description, sliceCount, spacingLabel, seriesDate } =
        describeUnloadedImage(image, intl);
    const handleClick = needsReformat ? onReformat : onDownload;

    return (
        <SelectableListItem isSelected={isSelected} onClick={handleClick}>
            <UnloadedImageTooltip image={image} onDelete={onDelete}>
                <Box display="grid">
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        width="100%"
                        overflow="hidden"
                    >
                        {needsReformat ? (
                            <WarningAmberIcon
                                sx={{
                                    fontSize: 12,
                                    color: 'warning.main',
                                    flexShrink: 0,
                                }}
                            />
                        ) : (
                            <CloudDownload
                                sx={{
                                    fontSize: 12,
                                    color: 'grey.500',
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <ModalityChip
                            modality={image.modality}
                            size="small"
                            color={needsReformat ? 'warning' : 'default'}
                        />
                        <Typography
                            fontSize={12}
                            noWrap
                            flex={1}
                            minWidth={0}
                            title={showSensitiveInfo ? description : undefined}
                            color={
                                isSelected || isSelected === undefined
                                    ? 'text.primary'
                                    : 'text.secondary'
                            }
                        >
                            <SensitiveInfoTypography>
                                {description}
                            </SensitiveInfoTypography>
                        </Typography>
                        {sliceCount !== undefined && (
                            <Typography
                                fontSize={10}
                                color="text.secondary"
                                whiteSpace="nowrap"
                                flexShrink={0}
                            >
                                {intl.formatMessage(
                                    { id: 'image.sliceCount' },
                                    { count: sliceCount }
                                )}
                            </Typography>
                        )}
                    </Box>
                    {(spacingLabel || seriesDate) && (
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            gap={0.5}
                            width="100%"
                            overflow="hidden"
                        >
                            {spacingLabel && (
                                <Typography
                                    fontSize={10}
                                    color="text.secondary"
                                    noWrap
                                >
                                    {spacingLabel}
                                </Typography>
                            )}
                            {seriesDate && (
                                <Typography
                                    fontSize={10}
                                    color="text.secondary"
                                    whiteSpace="nowrap"
                                    flexShrink={0}
                                >
                                    <SensitiveInfoTypography>
                                        {formatSeriesDate(seriesDate)}
                                    </SensitiveInfoTypography>
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </UnloadedImageTooltip>
        </SelectableListItem>
    );
}
